import { useTranslation, Trans } from 'react-i18next';
import React, { useState, useEffect, useRef } from 'react';
import {
  deleteWordpressRow,
  fetchWordpressFromDb,
  generateApiKey,
  syncWordpress,
  syncAllWordpress,
} from '../api/wordpressChannel';
import { getWpPluginUrl } from '../api/utils.js';
import ChannelIcon from './ChannelIcon';

// ─── Icons (local) ───────────────────────────────────────────────────────────
const CopyIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);
const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

// ═════════════════════════════════════════════════════════════════════════════
//  WORDPRESS – SHARED PLUGIN SETUP PANEL
//  Auto-generates an API key (unless one already exists), shows install
//  instructions, and polls the backend until the WordPress plugin pings home
//  with the site URL. Used both in Channels > WordPress and in the onboarding
//  flow so the two stay in sync.
// ═════════════════════════════════════════════════════════════════════════════
export const WordPressPluginSetupPanel = ({ onConnected, autoGenerate = true }) => {
  const { t } = useTranslation('channels');
  const [apiKey, setApiKey] = useState('');
  const [keyLoading, setKeyLoading] = useState(false);
  const [keyCopied, setKeyCopied] = useState(false);
  const [hasExistingKey, setHasExistingKey] = useState(false);
  const [wpConnected, setWpConnected] = useState(false);
  const [error, setError] = useState('');
  const pollRef = useRef(null);
  const initRan = useRef(false);

  // ── Initial load: figure out current state, auto-generate if needed ──────
  useEffect(() => {
    if (initRan.current) return;
    initRan.current = true;
    let cancelled = false;

    (async () => {
      try {
        const result = await fetchWordpressFromDb();
        if (cancelled) return;

        // Already fully connected — bubble up and stop.
        if (result.exists && result.wordpress_url) {
          setWpConnected(true);
          if (onConnected) onConnected(result.wordpress_url, result.accountData);
          return;
        }

        // Row exists with a key but no site_url yet — show "waiting" state.
        // Do NOT regenerate: that would invalidate a key the user may have
        // already pasted into their WordPress plugin.
        if (result.accountData?.has_api_key) {
          setHasExistingKey(true);
          return;
        }

        // No key yet — auto-generate if enabled.
        if (autoGenerate) {
          setKeyLoading(true);
          try {
            const data = await generateApiKey();
            if (!cancelled) setApiKey(data.api_key || '');
          } catch (e) {
            if (!cancelled) setError(e?.message || t('wordpress.keyGenerateFailed'));
          } finally {
            if (!cancelled) setKeyLoading(false);
          }
        }
      } catch (e) {
        if (!cancelled) setError(e?.message || t('wordpress.networkError'));
      }
    })();

    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Poll every 5s for the plugin to ping back with a site URL ────────────
  useEffect(() => {
    if (wpConnected) return;
    const check = async () => {
      try {
        const result = await fetchWordpressFromDb();
        if (result.exists && result.wordpress_url) {
          setWpConnected(true);
          if (onConnected) onConnected(result.wordpress_url, result.accountData);
        }
      } catch { /* ignore transient errors */ }
    };
    pollRef.current = setInterval(check, 5000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [wpConnected, onConnected]);

  const handleCopy = () => {
    if (!apiKey) return;
    navigator.clipboard.writeText(apiKey);
    setKeyCopied(true);
    setTimeout(() => setKeyCopied(false), 2000);
  };

  const handleRotate = async () => {
    if (!window.confirm(t('wordpress.rotateConfirm'))) return;
    setKeyLoading(true);
    setError('');
    try {
      const data = await generateApiKey();
      setApiKey(data.api_key || '');
      setHasExistingKey(false);
    } catch (e) {
      setError(e?.message || t('wordpress.keyGenerateFailed'));
    } finally {
      setKeyLoading(false);
    }
  };

  // Defensive "already connected" state — parent normally swaps in
  // WordPressConnectedPanel instead, but this makes the panel safe to mount
  // standalone anywhere.
  if (wpConnected) {
    return (
      <div className="wp-plugin-setup wp-plugin-setup--connected">
        <div className="wp-plugin-setup__status wp-plugin-setup__status--connected">
          <CheckIcon />
          <span>{t('wordpress.pluginConnected')}</span>
        </div>
        <style>{`
          .wp-plugin-setup--connected { padding: 20px 22px; }
          .wp-plugin-setup__status { display: flex; align-items: center; gap: 8px; padding: 10px 14px; border-radius: 8px; font-size: .84rem; font-weight: 500; background: #f3f4f6; color: #6b7280; }
          .wp-plugin-setup__status--connected { background: #ecfdf5; color: #059669; }
        `}</style>
      </div>
    );
  }

  return (
    <div className="wp-plugin-setup">
      {/* API Key */}
      <div className="wp-plugin-setup__key-section">
        <label className="wp-plugin-setup__label">{t('wordpress.yourApiKey')}</label>

        {hasExistingKey && !apiKey ? (
          <div className="wp-plugin-setup__existing">
            <p className="wp-plugin-setup__existing-text">{t('wordpress.keyAlreadyGenerated')}</p>
            <button
              type="button"
              className="wp-plugin-setup__rotate-btn"
              onClick={handleRotate}
              disabled={keyLoading}
            >
              {keyLoading ? t('wordpress.generatingKey') : t('wordpress.rotateKey')}
            </button>
          </div>
        ) : (
          <div className="wp-plugin-setup__key-row">
            <input
              type="text"
              className="wp-plugin-setup__key-input"
              value={keyLoading ? '...' : apiKey || t('wordpress.generatingKey')}
              readOnly
              disabled
            />
            <button
              type="button"
              className="wp-plugin-setup__copy-btn"
              onClick={handleCopy}
              disabled={!apiKey}
              title={t('wordpress.copyKey')}
            >
              {keyCopied ? <CheckIcon /> : <CopyIcon />}
              {keyCopied ? t('wordpress.keyCopied') : t('wordpress.copyKey')}
            </button>
          </div>
        )}

        {apiKey && (
          <p className="wp-plugin-setup__warning">{t('wordpress.saveKeyWarning')}</p>
        )}
        {error && <p className="wp-plugin-setup__error">{error}</p>}
      </div>

      {/* Instructions */}
      <ol className="wp-plugin-setup__steps">
        <li>{t('wordpress.installStep1')}</li>
        <li>
          <Trans
            i18nKey="wordpress.installStep2"
            t={t}
            components={{
              downloadLink: getWpPluginUrl()
                ? <a href={getWpPluginUrl()} target="_blank" rel="noreferrer" style={{ color: '#21759b', fontWeight: 500 }} />
                : <span />
            }}
          />
        </li>
        <li>{t('wordpress.installStep3')}</li>
      </ol>

      {/* Connection polling status */}
      <div className="wp-plugin-setup__status">
        <div className="wp-plugin-setup__status-spinner" />
        <span>{t('wordpress.waitingForConnection')}</span>
      </div>

      <style>{`
        .wp-plugin-setup { padding: 16px 22px 22px; }
        .wp-plugin-setup__key-section { margin-bottom: 16px; }
        .wp-plugin-setup__label { display: block; font-size: .82rem; font-weight: 500; color: #6b7280; margin-bottom: 6px; }
        .wp-plugin-setup__key-row { display: flex; gap: 8px; align-items: center; }
        .wp-plugin-setup__key-input { flex: 1; padding: 8px 12px; font-size: .85rem; font-family: monospace; border: 1px solid #d1d5db; border-radius: 6px; background: #f9fafb; color: #374151; outline: none; }
        .wp-plugin-setup__key-input:disabled { opacity: .8; cursor: default; }
        .wp-plugin-setup__copy-btn { display: inline-flex; align-items: center; gap: 5px; padding: 7px 14px; font-size: .82rem; font-weight: 500; border: 1px solid #d1d5db; border-radius: 6px; background: #fff; color: #374151; cursor: pointer; transition: all .15s; white-space: nowrap; }
        .wp-plugin-setup__copy-btn:hover:not(:disabled) { border-color: #9ca3af; background: #f3f4f6; }
        .wp-plugin-setup__copy-btn:disabled { opacity: .5; cursor: not-allowed; }
        .wp-plugin-setup__existing { display: flex; flex-direction: column; gap: 8px; padding: 12px 14px; background: #fffbeb; border: 1px solid #fde68a; border-radius: 8px; }
        .wp-plugin-setup__existing-text { margin: 0; font-size: .84rem; color: #92400e; line-height: 1.5; }
        .wp-plugin-setup__rotate-btn { align-self: flex-start; padding: 6px 14px; font-size: .8rem; font-weight: 600; border-radius: 6px; border: 1px solid #f59e0b; background: #fff; color: #b45309; cursor: pointer; transition: all .15s; }
        .wp-plugin-setup__rotate-btn:hover:not(:disabled) { background: #fef3c7; }
        .wp-plugin-setup__rotate-btn:disabled { opacity: .5; cursor: not-allowed; }
        .wp-plugin-setup__warning { margin: 6px 0 0; font-size: .76rem; color: #b45309; }
        .wp-plugin-setup__error { margin: 6px 0 0; font-size: .78rem; color: #dc2626; }
        .wp-plugin-setup__steps { margin: 0 0 16px; padding-left: 20px; font-size: .85rem; color: #374151; line-height: 1.8; }
        .wp-plugin-setup__steps li { margin-bottom: 2px; }
        .wp-plugin-setup__status { display: flex; align-items: center; gap: 8px; padding: 10px 14px; border-radius: 8px; font-size: .84rem; font-weight: 500; background: #f3f4f6; color: #6b7280; }
        .wp-plugin-setup__status-spinner { width: 14px; height: 14px; border: 2px solid #d1d5db; border-top-color: #6b7280; border-radius: 50%; animation: wpSetupSpin .7s linear infinite; }
        @keyframes wpSetupSpin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
//  WORDPRESS – SETUP PANEL (Channels page wrapper)
//  Thin wrapper around WordPressPluginSetupPanel that adds the channel card
//  header (WordPress icon, title, help link) so it matches the other channels.
// ═════════════════════════════════════════════════════════════════════════════
export const WordPressSetupPanel = ({ onConnected }) => {
  const { t } = useTranslation('channels');

  return (
    <div className="wp-s">
      <div className="wp-s__header">
        <div className="wp-s__header-left">
          <ChannelIcon channel="wordpress" size={28} />
          <span className="wp-s__title">{t('wordpress.title')}</span>
        </div>
        <a href="https://aerochat.ai/help/how-to-connect-to-wordpress" target="_blank" rel="noreferrer" className="wp-s__help">{t('wordpress.help')}</a>
      </div>
      <WordPressPluginSetupPanel onConnected={onConnected} />
      <style>{`
        .wp-s { display:flex; flex-direction:column; }
        .wp-s__header { display:flex; align-items:center; justify-content:space-between; padding:16px 22px 4px; }
        .wp-s__header-left { display:flex; align-items:center; gap:10px; }
        .wp-s__title { font-size:1.05rem; font-weight:600; color:#111827; }
        .wp-s__help { font-size:.82rem; color:#6b7280; text-decoration:none; }
        .wp-s__help:hover { text-decoration:underline; color:#374151; }
      `}</style>
    </div>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
//  WORDPRESS – CONNECTED PANEL
//  Shown once the plugin has pinged home and the site URL is populated.
//  A connected plugin by definition has a working API key, so this panel
//  never offers to "generate" one — only to rotate (with a warning).
// ═════════════════════════════════════════════════════════════════════════════
export const WordPressConnectedPanel = ({ wpUrl, onDisconnect, accountData }) => {
  const { t } = useTranslation('channels');
  const [rotatedKey, setRotatedKey] = useState('');
  const [keyLoading, setKeyLoading] = useState(false);
  const [keyCopied, setKeyCopied] = useState(false);
  const [syncLoading, setSyncLoading] = useState('');
  const [syncMsg, setSyncMsg] = useState('');
  const [showSetup, setShowSetup] = useState(false);
  const [rotateError, setRotateError] = useState('');

  function handleDisconnect() {
    if (window.confirm(t('wordpress.confirmDisconnect'))) {
      deleteWordpressRow();
      if (onDisconnect) onDisconnect();
    }
  }

  async function handleRotateKey() {
    if (!window.confirm(t('wordpress.rotateConfirm'))) return;
    setKeyLoading(true);
    setRotateError('');
    try {
      const data = await generateApiKey();
      setRotatedKey(data.api_key || '');
    } catch (e) {
      setRotateError(e?.message || t('wordpress.keyGenerateFailed'));
    } finally {
      setKeyLoading(false);
    }
  }

  function handleCopyKey() {
    if (rotatedKey) {
      navigator.clipboard.writeText(rotatedKey);
      setKeyCopied(true);
      setTimeout(() => setKeyCopied(false), 2000);
    }
  }

  async function handleSync(tab) {
    setSyncLoading(tab);
    setSyncMsg('');
    try {
      if (tab === 'all') {
        await syncAllWordpress();
        setSyncMsg(t('wordpress.syncAllStarted'));
      } else {
        await syncWordpress(tab);
        setSyncMsg(t('wordpress.syncComplete', { tab }));
      }
    } catch (e) {
      setSyncMsg(e.message || t('wordpress.syncFailed'));
    } finally {
      setSyncLoading('');
    }
  }

  const lastSync = accountData?.last_sync_at;
  const syncStatus = accountData?.sync_status || 'idle';

  return (
    <div className="wpc-panel">
      <div className="wpc-panel__top">
        <div className="wpc-panel__info">
          <div className="wpc-panel__icon-wrap">
            <ChannelIcon channel="wordpress" size={30} />
          </div>
          <div>
            <p className="wpc-panel__label">{t('wordpress.connectedTo')}</p>
            <a href={wpUrl} target="_blank" rel="noreferrer" className="wpc-panel__url">{wpUrl || 'yoursite.com'}</a>
          </div>
        </div>
        <span className="wpc-badge"><span className="wpc-dot" />{syncStatus === 'syncing' ? t('wordpress.syncing') : t('wordpress.active')}</span>
      </div>

      <div className="wpc-panel__body">
        {/* API Key Section — plugin is already connected, so only offer rotate */}
        <div className="wpc-section">
          <h4 className="wpc-section__title">{t('wordpress.pluginApiKey')}</h4>
          <p className="wpc-section__desc">{t('wordpress.rotateKeyDesc')}</p>

          {rotatedKey ? (
            <>
              <div className="wpc-key-row">
                <code className="wpc-key-code">{rotatedKey}</code>
                <button className="wpc-btn-sm" onClick={handleCopyKey}>
                  {keyCopied ? t('wordpress.keyCopied') : t('wordpress.copyKey')}
                </button>
              </div>
              <p className="wpc-key-warning">{t('wordpress.saveKeyWarning')}</p>
            </>
          ) : (
            <button
              className="wpc-btn-rotate"
              onClick={handleRotateKey}
              disabled={keyLoading}
            >
              {keyLoading ? t('wordpress.generatingKey') : t('wordpress.rotateKey')}
            </button>
          )}
          {rotateError && <p className="wpc-key-error">{rotateError}</p>}
        </div>

        {/*
          Sync Controls — hidden for now. Content sync is driven entirely from
          the WordPress plugin side (realtime hooks on post/product/coupon/
          shipping save + a twice-daily cron in aerochat.php), so surfacing a
          "Sync Now" button here only invites confusion about which side owns
          the schedule. Re-enable by flipping this flag if we ever need a
          manual pull-from-admin-UI path again.
        */}
        {false && (
          <div className="wpc-section" style={{marginTop:'20px'}}>
            <h4 className="wpc-section__title">{t('wordpress.knowledgeBaseSync')}</h4>
            {lastSync && <p className="wpc-section__desc">{t('wordpress.lastSynced', { when: new Date(lastSync).toLocaleString() })}</p>}
            <div style={{display:'flex',flexWrap:'wrap',gap:'8px',marginTop:'8px'}}>
              {['pages', 'products', 'promotions'].map(tab => (
                <button key={tab} className="wpc-btn-sm" disabled={!!syncLoading} onClick={() => handleSync(tab)}>
                  {syncLoading === tab ? t('wordpress.syncing') : t(`wordpress.sync_${tab}`)}
                </button>
              ))}
              <button className="wpc-btn-primary" disabled={!!syncLoading} onClick={() => handleSync('all')}>
                {syncLoading === 'all' ? t('wordpress.syncing') : t('wordpress.syncAll')}
              </button>
            </div>
            {syncMsg && <p style={{fontSize:'.84rem',color:'#6b7280',marginTop:'8px'}}>{syncMsg}</p>}
          </div>
        )}

        {/* Setup Instructions (collapsible) */}
        <div className="wpc-section" style={{marginTop:'20px'}}>
          <button className="wpc-btn-link" onClick={() => setShowSetup(!showSetup)}>
            {showSetup ? t('wordpress.hideInstructions') : t('wordpress.showInstructions')}
          </button>
          {showSetup && (
            <ol style={{fontSize:'.84rem',color:'#374151',lineHeight:'1.8',paddingLeft:'20px',marginTop:'8px'}}>
              <li>{t('wordpress.installStep1')}</li>
              <li>
                <Trans
                  i18nKey="wordpress.installStep2"
                  t={t}
                  components={{
                    downloadLink: getWpPluginUrl()
                      ? <a href={getWpPluginUrl()} target="_blank" rel="noreferrer" style={{ color: '#21759b', fontWeight: 500 }} />
                      : <span />
                  }}
                />
              </li>
              <li>{t('wordpress.installStep3')}</li>
            </ol>
          )}
        </div>

        {/* Disconnect */}
        <div className="wpc-section" style={{marginTop:'24px',paddingTop:'20px',borderTop:'1px solid #e5e7eb'}}>
          <div className="wpc-action">
            <div className="wpc-action__icon">
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                <path d="M10 11v6M14 11v6M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
              </svg>
            </div>
            <div className="wpc-action__body">
              <h4 className="wpc-action__title">{t('wordpress.disconnectTitle')}</h4>
              <p className="wpc-action__desc">{t('wordpress.disconnectDesc')}</p>
              <button className="wpc-btn" onClick={handleDisconnect}>{t('wordpress.disconnectButton')}</button>
            </div>
          </div>
        </div>
      </div>
      <style>{`
        .wpc-panel{display:flex;flex-direction:column}
        .wpc-panel__top{display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:12px;padding:24px 30px;border-bottom:1px solid var(--border-color,#e5e7eb)}
        .wpc-panel__info{display:flex;align-items:center;gap:16px}
        .wpc-panel__icon-wrap{width:52px;height:52px;border-radius:13px;background:linear-gradient(135deg,#e8f4fd,#d0eaf9);border:1.5px solid #b8ddf5;display:flex;align-items:center;justify-content:center;flex-shrink:0}
        .wpc-panel__label{margin:0 0 3px;font-size:.78rem;color:var(--text-secondary,#6b7280);text-transform:uppercase;letter-spacing:.05em}
        .wpc-panel__url{display:block;font-size:1rem;font-weight:700;color:#21759b;text-decoration:none;word-break:break-all}
        .wpc-panel__url:hover{text-decoration:underline}
        .wpc-badge{display:inline-flex;align-items:center;gap:7px;padding:6px 16px;background:#dcfce7;color:#16a34a;border-radius:999px;font-size:.8rem;font-weight:600;white-space:nowrap}
        .wpc-dot{width:7px;height:7px;border-radius:50%;background:#16a34a;animation:wpcPulse 2s ease-in-out infinite}
        @keyframes wpcPulse{0%,100%{opacity:1}50%{opacity:.3}}
        .wpc-panel__body{padding:24px 30px}
        .wpc-section__title{margin:0 0 5px;font-size:.95rem;font-weight:600;color:var(--text-primary,#111827)}
        .wpc-section__desc{margin:0 0 10px;font-size:.84rem;color:var(--text-secondary,#6b7280);line-height:1.5}
        .wpc-key-row{display:flex;align-items:center;gap:8px;margin-bottom:6px}
        .wpc-key-code{padding:6px 12px;background:#f3f4f6;border-radius:6px;font-size:.82rem;word-break:break-all;flex:1;font-family:monospace}
        .wpc-key-warning{margin:4px 0 0;font-size:.78rem;color:#b45309}
        .wpc-key-error{margin:6px 0 0;font-size:.78rem;color:#dc2626}
        .wpc-btn-rotate{padding:7px 18px;font-size:.84rem;font-weight:600;border-radius:6px;cursor:pointer;border:1px solid #f59e0b;color:#b45309;background:#fff;transition:all .15s}
        .wpc-btn-rotate:hover:not(:disabled){background:#fffbeb}
        .wpc-btn-rotate:disabled{opacity:.5;cursor:not-allowed}
        .wpc-action{display:flex;gap:16px;align-items:flex-start}
        .wpc-action__icon{flex-shrink:0;width:40px;height:40px;border-radius:10px;background:#fee2e2;display:flex;align-items:center;justify-content:center;margin-top:2px}
        .wpc-action__body{flex:1}
        .wpc-action__title{margin:0 0 5px;font-size:.95rem;font-weight:600;color:var(--text-primary,#111827)}
        .wpc-action__desc{margin:0 0 14px;font-size:.84rem;color:var(--text-secondary,#6b7280);line-height:1.5}
        .wpc-btn{display:inline-flex;align-items:center;gap:6px;padding:8px 22px;font-size:.85rem;font-weight:600;border-radius:8px;cursor:pointer;border:1.5px solid #ef4444;color:#ef4444;background:transparent;transition:all .15s}
        .wpc-btn:hover{background:#fee2e2}
        .wpc-btn-sm{padding:6px 16px;font-size:.82rem;font-weight:500;border-radius:6px;cursor:pointer;border:1px solid #d1d5db;color:#374151;background:#fff;transition:all .15s}
        .wpc-btn-sm:hover:not(:disabled){background:#f3f4f6;border-color:#9ca3af}
        .wpc-btn-sm:disabled{opacity:.5;cursor:not-allowed}
        .wpc-btn-primary{padding:7px 20px;font-size:.85rem;font-weight:600;border-radius:6px;cursor:pointer;border:none;color:#fff;background:#21759b;transition:all .15s}
        .wpc-btn-primary:hover:not(:disabled){background:#1a5f7e}
        .wpc-btn-primary:disabled{opacity:.5;cursor:not-allowed}
        .wpc-btn-link{padding:0;font-size:.84rem;font-weight:500;color:#21759b;background:none;border:none;cursor:pointer;text-decoration:underline}
        .wpc-btn-link:hover{color:#1a5f7e}
        .wpc-btn-link:disabled{opacity:.5;cursor:not-allowed}
        @media(max-width:640px){.wpc-panel__top,.wpc-panel__body{padding:18px 20px}}
      `}</style>
    </div>
  );
};

export default WordPressSetupPanel;
